/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { FaMicrophoneAlt } from "react-icons/fa";
import CustomAudioPlayer from "./CustomAudioPlayer";

const MAX_MJPEG_RETRIES = 3;
const RETRY_MJPEG_DELAY_BASE = 2500;
const STREAM_SWITCH_CLEANUP_DELAY = 500; // ms between stopping old & starting new

const CameraFeed = ({
  displayName,
  status,
  streamUrl,
  playbackSource,
  isRtspAudioActive,
  rtspOriginalUrl,
  isAudioOnly = false, // optional flag
}) => {
  /* ─── de-structure status object ─────────────────────────────── */
  const {
    is_running: isOverallProcessRunning,
    current_mode: backendModeName,
  } = status || {};

  /* ─── refs ────────────────────────────────────────────────────── */
  const imgRef = useRef(null);
  const mjpegRetryTimeoutRef = useRef(null);
  const objectUrlRef = useRef(null);
  const intendedStreamUrlRef = useRef(null);
  const mountedRef = useRef(false);
  const cleanupTimeoutRef = useRef(null);
  const previousStreamUrlRef = useRef(null);

  /* ─── MJPEG state ────────────────────────────────────────────── */
  const [mjpegSrcToLoad, setMjpegSrcToLoad] = useState(null);
  const [isLoadingMjpeg, setIsLoadingMjpeg] = useState(false);
  const [mjpegLoadError, setMjpegLoadError] = useState(null);
  const [mjpegRetryCount, setMjpegRetryCount] = useState(0);
  const [mjpegImgElementKey, setMjpegImgElementKey] = useState(Date.now());
  const [isStreamSwitching, setIsStreamSwitching] = useState(false);

  /* ─── Media-file / playback state ────────────────────────────── */
  const [mediaElementSrc, setMediaElementSrc] = useState(null);
  const [mediaElementType, setMediaElementType] = useState(null);
  const [isMediaElementLoading, setIsMediaElementLoading] = useState(false);
  const [mediaElementError, setMediaElementError] = useState(null);

  /* ─── quick flags ────────────────────────────────────────────── */
  const isCSMDisplayMode = displayName?.includes("Customer Service Monitor");
  const isBounceDetectorDisplayMode = displayName?.includes("Bounce Detector");

  /** Live-Mic audio-only?
   * – explicit flag from parent **or**
   * – CSM/Bounce task that’s running, has no streamUrl & nothing to play */
  const liveMicAudioOnly =
    isAudioOnly ||
    ((isCSMDisplayMode || isBounceDetectorDisplayMode) &&
      isOverallProcessRunning &&
      !streamUrl &&
      !playbackSource);

  /* only attempt MJPEG if a streamUrl exists and it should be visualised */
  const shouldAttemptMjpeg =
    !!streamUrl &&
    (
      /* video agents */
      (!isCSMDisplayMode && !isBounceDetectorDisplayMode && isOverallProcessRunning) ||
      /* audio agents – show whenever we have a companion stream */
      (isCSMDisplayMode || isBounceDetectorDisplayMode)
    );

  /* ─── lifecycle ──────────────────────────────────────────────── */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      [mjpegRetryTimeoutRef, cleanupTimeoutRef].forEach((ref) => {
        if (ref.current) {
          clearTimeout(ref.current);
          ref.current = null;
        }
      });
    };
  }, []);

  /* helper to clear any <img> stream cleanly */
  const forceCleanupOldStream = useCallback(() => {
    if (imgRef.current) {
      imgRef.current.src = "";
      imgRef.current.removeAttribute("src");
    }
    if (mjpegRetryTimeoutRef.current) {
      clearTimeout(mjpegRetryTimeoutRef.current);
      mjpegRetryTimeoutRef.current = null;
    }
    setMjpegSrcToLoad(null);
    setIsLoadingMjpeg(false);
    setMjpegLoadError(null);
    setMjpegRetryCount(0);
    if (window.gc) window.gc(); // chrome --js-flags="--expose-gc"
  }, []);

  /* ─── MJPEG start / retry / switch ───────────────────────────── */
  useEffect(() => {
    if (!mountedRef.current) return;

    const urlChanged = previousStreamUrlRef.current !== streamUrl;
    previousStreamUrlRef.current = streamUrl;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    forceCleanupOldStream();
    intendedStreamUrlRef.current = null;

    if (!shouldAttemptMjpeg) return;

    const startNewStream = () => {
      if (!mountedRef.current) return;
      intendedStreamUrlRef.current = streamUrl;
      setIsLoadingMjpeg(true);
      setIsStreamSwitching(false);

      const key = Date.now() + Math.random();
      const qs = new URLSearchParams({
        t: Date.now(),
        key,
        retry: 0,
        uid: Math.random().toString(36).slice(2, 9),
        sw: urlChanged ? 1 : 0,
      });
      setMjpegImgElementKey(key);
      setMjpegSrcToLoad(`${streamUrl}?${qs.toString()}`);
    };

    if (urlChanged) {
      setIsStreamSwitching(true);
      cleanupTimeoutRef.current = setTimeout(
        startNewStream,
        STREAM_SWITCH_CLEANUP_DELAY
      );
    } else {
      startNewStream();
    }
  }, [streamUrl, shouldAttemptMjpeg, forceCleanupOldStream]);

  const handleMjpegLoad = useCallback(() => {
    if (
      !mountedRef.current ||
      intendedStreamUrlRef.current !== streamUrl
    )
      return;
    setIsLoadingMjpeg(false);
    setMjpegLoadError(null);
    setMjpegRetryCount(0);
  }, [streamUrl]);

  const handleMjpegError = useCallback(() => {
    if (
      !mountedRef.current ||
      intendedStreamUrlRef.current !== streamUrl ||
      !shouldAttemptMjpeg
    ) {
      return;
    }

    const nextAttempt = mjpegRetryCount + 1;
    if (nextAttempt > MAX_MJPEG_RETRIES) {
      setMjpegLoadError(`Video failed after ${MAX_MJPEG_RETRIES} retries.`);
      setIsLoadingMjpeg(false);
      return;
    }

    setMjpegRetryCount(nextAttempt);
    setMjpegLoadError(
      `Stream error – retry ${nextAttempt}/${MAX_MJPEG_RETRIES}`
    );
    setIsLoadingMjpeg(true);

    if (mjpegRetryTimeoutRef.current)
      clearTimeout(mjpegRetryTimeoutRef.current);
    mjpegRetryTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      forceCleanupOldStream();

      const key = Date.now() + Math.random();
      const qs = new URLSearchParams({
        t: Date.now(),
        key,
        retry: nextAttempt,
        uid: Math.random().toString(36).slice(2, 9),
      });
      setMjpegImgElementKey(key);
      setMjpegSrcToLoad(`${streamUrl}?${qs.toString()}`);
    }, RETRY_MJPEG_DELAY_BASE * Math.pow(1.8, mjpegRetryCount));
  }, [streamUrl, mjpegRetryCount, shouldAttemptMjpeg, forceCleanupOldStream]);

  /* ─── Media-file / static playback initialisation ────────────── */
  useEffect(() => {
    if (!mountedRef.current) return;

    setIsMediaElementLoading(true);
    setMediaElementError(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setMediaElementSrc(null);
    setMediaElementType(null);

    if (
      (isCSMDisplayMode || isBounceDetectorDisplayMode) &&
      !shouldAttemptMjpeg &&
      playbackSource
    ) {
      if (playbackSource instanceof File) {
        const objUrl = URL.createObjectURL(playbackSource);
        objectUrlRef.current = objUrl;
        setMediaElementSrc(objUrl);
        setMediaElementType(
          playbackSource.type.startsWith("audio/") ? "audio" : "video"
        );
      } else if (typeof playbackSource === "string") {
        if (playbackSource.toLowerCase().startsWith("rtsp://")) {
          setMediaElementSrc("rtsp_not_playable_static");
        } else {
          setMediaElementSrc(playbackSource);
          const ext = playbackSource
            .split(/[#?]/)[0]
            .split(".")
            .pop()
            ?.toLowerCase();
          const audioExts = [
            "mp3",
            "wav",
            "ogg",
            "aac",
            "m4a",
            "flac",
          ];
          const videoExts = ["mp4", "webm", "ogv", "mov"];
          setMediaElementType(
            audioExts.includes(ext)
              ? "audio"
              : videoExts.includes(ext)
              ? "video"
              : null
          );
          if (!audioExts.includes(ext) && !videoExts.includes(ext))
            setMediaElementError("Unsupported URL type for playback.");
        }
      } else {
        setMediaElementError("Invalid playback source provided.");
      }
    }
    setIsMediaElementLoading(false);

    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [
    playbackSource,
    shouldAttemptMjpeg,
    isCSMDisplayMode,
    isBounceDetectorDisplayMode,
  ]);

  /* ─── HEADER status text / colour class ──────────────────────── */
  let headerTxt = "IDLE";
  let headerCls = "status-idle";

  if (displayName === "No Active Process" || !status) {
    headerTxt = "NO ACTIVE PROCESS";
  } else if (isCSMDisplayMode || isBounceDetectorDisplayMode) {
    const modeName = isBounceDetectorDisplayMode
      ? "BOUNCE DETECTOR"
      : "CUSTOMER SERVICE MONITOR";

    if (isRtspAudioActive || streamUrl) { // ← changed
      if (shouldAttemptMjpeg) {
        if (isStreamSwitching) headerTxt = "SWITCHING STREAM";
        else if (isLoadingMjpeg)
          headerTxt = `${modeName} (VIDEO CONNECTING)`;
        else if (mjpegLoadError)
          headerTxt = `${modeName} (VIDEO ERROR)`;
        else if (mjpegSrcToLoad)
          headerTxt = `LIVE ${modeName} AUDIO & VIDEO`;
        else headerTxt = `${modeName} (VIDEO PENDING)`;
        headerCls = isStreamSwitching
          ? "status-switching"
          : isLoadingMjpeg
          ? "status-connecting"
          : mjpegLoadError
          ? "status-error"
          : mjpegSrcToLoad
          ? "status-processing"
          : "status-warning";
      } else {
        headerTxt = `LIVE ${modeName} AUDIO ONLY`;
        headerCls = "status-audio-mode";
      }
    } else if (liveMicAudioOnly) {
      headerTxt = `LIVE ${modeName} MIC`;
      headerCls = "status-audio-mode";
    } else if (playbackSource) {
      const backendProcStatus = status?.status;
      const done =
        backendProcStatus === "completed" ||
        backendProcStatus === "processed";
      if (!status?.is_running && done) {
        headerTxt = `ANALYSIS COMPLETE (${modeName} MEDIA)`;
        headerCls = "status-completed";
      } else if (status?.is_running) {
        headerTxt = `ANALYZING MEDIA (${modeName})`;
        headerCls = "status-audio-mode";
      } else if (mediaElementSrc) {
        headerTxt = `MEDIA PLAYER READY (${modeName})`;
        headerCls = "status-audio-mode";
      }
    }
  } else if (shouldAttemptMjpeg) {
    if (isStreamSwitching) {
      headerTxt = "SWITCHING STREAM";
      headerCls = "status-switching";
    } else if (isLoadingMjpeg) {
      headerTxt = "CONNECTING VIDEO";
      headerCls = "status-connecting";
    } else if (mjpegLoadError) {
      headerTxt = "VIDEO STREAM ERROR";
      headerCls = "status-error";
    } else if (mjpegSrcToLoad) {
      headerTxt = "LIVE VIDEO FEED";
      headerCls = "status-processing";
    } else {
      headerTxt = "VIDEO PENDING";
      headerCls = "status-warning";
    }
  } else if (isOverallProcessRunning) {
    headerTxt = backendModeName
      ? `${backendModeName.toUpperCase()} ACTIVE (NO VISUAL STREAM)`
      : "PROCESS ACTIVE (NO VISUAL STREAM)";
    headerCls = "status-warning";
  }

  /* ─── MAIN content area ──────────────────────────────────────── */
  let feedContent = null;

  if (shouldAttemptMjpeg) {
    /* live MJPEG */
    feedContent = (
      <div className="mjpeg-wrapper">
        {mjpegSrcToLoad && !isStreamSwitching && (
          <img
            key={mjpegImgElementKey}
            ref={imgRef}
            src={mjpegSrcToLoad}
            alt={`Live: ${displayName}`}
            onLoad={handleMjpegLoad}
            onError={handleMjpegError}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              objectFit: "contain",
              background: isLoadingMjpeg ? "#2c2c2c" : "#000",
            }}
          />
        )}

        {(isLoadingMjpeg || isStreamSwitching) && (
          <div className="overlay loading">
            {isStreamSwitching
              ? `Switching to "${displayName}"…`
              : `Connecting to "${displayName}"… ${
                  mjpegRetryCount
                    ? `(Retry ${mjpegRetryCount}/${MAX_MJPEG_RETRIES})`
                    : ""
                }`}
          </div>
        )}

        {mjpegLoadError && !isStreamSwitching && (
          <div className="overlay error">{mjpegLoadError}</div>
        )}
      </div>
    );
  } else if (isCSMDisplayMode || isBounceDetectorDisplayMode) {
    const modeName = isBounceDetectorDisplayMode
      ? "Bounce Detector"
      : "Customer Service Monitor";

    if (isMediaElementLoading) {
      feedContent = (
        <div className="no-stream-placeholder">Loading media…</div>
      );
    } else if (mediaElementError) {
      feedContent = (
        <div className="no-stream-placeholder load-error">
          {mediaElementError}
        </div>
      );
    } else if (mediaElementSrc === "rtsp_not_playable_static") {
      feedContent = (
        <div className="no-stream-placeholder">
          RTSP link used for analysis – browser cannot play RTSP.
          {rtspOriginalUrl && (
            <>
              <br />
              <em>Original: {rtspOriginalUrl}</em>
            </>
          )}
        </div>
      );
    } else if (
      isRtspAudioActive &&
      rtspOriginalUrl &&
      !shouldAttemptMjpeg
    ) {
      feedContent = (
        <div className="no-stream-placeholder">
          Processing live RTSP audio for {modeName} from:
          <br />
          <em>{rtspOriginalUrl}</em>
        </div>
      );
    } else if (liveMicAudioOnly) {
      feedContent = (
        <div className="audio-only-placeholder">
          <FaMicrophoneAlt size={96} color="#6c63ff" />
          <p style={{ marginTop: "1rem" }}>Live microphone streaming…</p>
        </div>
      );
    } else if (mediaElementSrc && mediaElementType === "audio") {
      feedContent = (
        <CustomAudioPlayer
          src={mediaElementSrc}
          autoPlay
          className="camera-feed-audio-player"
        />
      );
    } else if (mediaElementSrc && mediaElementType === "video") {
      feedContent = (
        <video
          controls
          autoPlay
          src={mediaElementSrc}
          className="video-player-feed"
          style={{ width: "100%", maxHeight: "450px" }}
        />
      );
    } else if (playbackSource) {
      feedContent = (
        <div className="no-stream-placeholder">
          Media preview not available for {modeName}. Analysis may be
          active or complete.
        </div>
      );
    } else {
      feedContent = (
        <div className="no-stream-placeholder">
          No media selected for {modeName}.
        </div>
      );
    }
  } else {
    /* generic fallbacks */
    if (isOverallProcessRunning) {
      feedContent = (
        <div className="no-stream-placeholder">
          {`"${displayName}" is active (no video stream output).`}
        </div>
      );
    } else {
      feedContent = (
        <div className="no-stream-placeholder">
          No active video process for "{displayName}".
        </div>
      );
    }
  }

  /* ─── markup ─────────────────────────────────────────────────── */
  return (
    <div className="camera-feed-container card-style">
      <div className="feed-header">
        <p className="feed-title">
          <span className={headerCls}>{headerTxt}</span>{" "}
          {displayName || "Feed Area"}
        </p>
      </div>
      <div className="stream-wrapper">{feedContent}</div>
    </div>
  );
};

/* ─── Prop-types & defaults ────────────────────────────────────── */
CameraFeed.propTypes = {
  displayName: PropTypes.string,
  status: PropTypes.shape({
    is_running: PropTypes.bool,
    current_mode: PropTypes.string,
    message: PropTypes.string,
    isRunning: PropTypes.bool,
    status: PropTypes.string,
  }),
  streamUrl: PropTypes.string,
  playbackSource: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isRtspAudioActive: PropTypes.bool,
  rtspOriginalUrl: PropTypes.string,
  isAudioOnly: PropTypes.bool,
};

CameraFeed.defaultProps = {
  displayName: "Feed Area",
  status: { is_running: false, current_mode: null, message: "N/A" },
  streamUrl: null,
  playbackSource: null,
  isRtspAudioActive: false,
  rtspOriginalUrl: null,
  isAudioOnly: false,
};

export default React.memo(CameraFeed);